<template>
    <div class="button is-large is-fullwidth" :class="getStatusClass(status)" @click="toggleDeliveryUpdateTab">
        <p :class="{ strikethrough: completed}">{{ unit }},
            {{ block }} {{ street }}</p>
        <div class="delivery-right">
            <p class="dynamic-circle"
                :class="{
                    'circle-1': consecutiveUndeliveredCount === 3,
                    'circle-2': consecutiveUndeliveredCount === 4,
                    'circle-3': consecutiveUndeliveredCount >= 5
                }"
                v-if="consecutiveUndeliveredCount >= 3"
                >
                {{ consecutiveUndeliveredCount }}</p>
            <span v-if="adminNotes" class="tag is-warning">
                <span class="icon is-small"><i class="fas fa-sticky-note"></i></span>
            </span>
            <span v-if="!!dietaryRequirement && isDisplayDietaryRequirementEnabled" class="tag is-success"> {{dietaryRequirement}} </span>
            <p :class="{ strikethrough: completed}"> <b>{{ quantity }}
                    <span style="color:blue" v-if="dryBag"> Dry</span>
                    <span v-else>bag</span>
                    <span v-if="quantity > 1">s</span></b>
            </p>
        </div>
    </div>

    <div class="modal" :class="[showDeliveryUpdateTab ? 'is-active' : '']">
        <div class="modal-background"></div>
        <div class="modal-card">
            <header class="modal-card-head">
                <p class="modal-card-title">Beneficiary ID: {{ beneficiaryID }} <span v-if="!!dietaryRequirement && isDisplayDietaryRequirementEnabled" class="tag is-success"> {{dietaryRequirement}} </span></p>
                <button class="delete" aria-label="close" @click="toggleDeliveryUpdateTab"></button>
            </header>
            <section class="modal-card-body">
                <table class="table is-fullwidth">
                    <tbody>
                        <tr>
                            <th>
                                Name
                            </th>
                            <td>
                                <span v-if="isCurrentUserAdmin">
                                    <router-link :to="editLink">{{ name }}</router-link>
                                </span>
                                <span v-else>{{ name }}</span>
                            </td>
                        </tr>
                        <tr>
                            <th>
                                Address
                            </th>
                            <td>
                                <p>{{ unit }} </p>
                                <p>{{ block }}, {{ street }}</p>
                            </td>
                        </tr>
                        <tr v-if="isCurrentUserAdmin">
                            <th>
                                Delivery Mode
                            </th>
                            <td>
                                <div v-if="selfCollection">Self Collection</div>
                                <div v-else-if="drivingDelivery">Driving Delivery
                                    <span v-if="deliverySort">| Sort {{ deliverySort }}</span>
                                </div>
                                <div v-else>Walking Delivery</div>
                            </td>
                        </tr>
                        <tr v-if="isCurrentUserAdmin && !selfCollection && deliveryTeam">
                            <th>
                                Delivery Team
                            </th>
                            <td>
                                {{ deliveryTeam }}
                            </td>
                        </tr>
                        <tr v-if="completed">
                            <th>
                                Status
                            </th>
                            <td>
                                {{ status }}
                            </td>
                        </tr>
                        <tr v-if="deliveryNotes">
                            <th>
                                Notes
                            </th>
                            <td>
                                {{ deliveryNotes }}
                            </td>
                        </tr>
                        <tr v-if="!isCurrentUserAdmin && adminNotes" class="has-background-warning-light">
                            <th class="has-text-warning-dark">
                                <span class="icon-text">
                                    <span class="icon"><i class="fas fa-sticky-note"></i></span>
                                    <span>Delivery Notes</span>
                                </span>
                            </th>
                            <td class="has-text-weight-semibold">{{ adminNotes }}</td>
                        </tr>
                        <tr v-if="isCurrentUserAdmin">
                            <th>
                                <span class="icon-text">
                                    <span class="icon has-text-warning"><i class="fas fa-sticky-note"></i></span>
                                    <span>Delivery Notes</span>
                                </span>
                            </th>
                            <td>
                                <textarea v-model="editableAdminNotes" class="textarea is-small" placeholder="Notes for volunteers"></textarea>
                                <button class="button is-small is-info mt-1" :class="{ 'is-loading': isSavingAdminNotes }" @click="saveAdminNotes">Save Notes</button>
                            </td>
                        </tr>
                        <tr v-if="completedAtTime">
                            <th>
                                Completed At
                            </th>
                            <td>
                                {{ completedAtTime.toDate() }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <footer class="modal-card-foot">

                <base-spinner v-if="isLoading"></base-spinner>

                <div v-else>

                    <div v-if="!!error" class="notification is-danger is-light">
                        <p><b>Something went wrong!</b></p>
                        <p>{{ error }}</p>
                    </div>

                    <div v-if="fdStatus == 'LIVE'">
                        <div v-if="!completed || canUpdateDelivery">

                            <div v-if="!selfCollection && isReusableBagFeatureEnabled">
                                <div v-if="bagStatus == 'HAS_NO_BAG'">
                                    <p>Leave this bag with the beneficiary.</p>
                                </div>
                                <div v-else>
                                    <div v-if="bagStatus == 'NOT_RETURNED'">
                                        <p class="warning">Beneficiary didn't return the empty bag last week. DO NOT leave
                                            the bag unless
                                            the previous one is returned.</p>
                                        <strong>Did you collect the previous bag?</strong>
                                    </div>
                                    <div v-else-if="bagStatus == 'HAS_A_BAG'">
                                        <strong>Have you collected the empty bag from the beneficiary?</strong>
                                    </div>
                                    <div class="control">
                                        <label class="radio">
                                            <input v-model="bagCollected" :value="true" type="radio">
                                            Yes
                                        </label>
                                        <label class="radio">
                                            <input v-model="bagCollected" :value="false" type="radio">
                                            No
                                        </label>
                                        <p v-if="isBagCollectionRequired && bagCollected == null" class="help is-danger">
                                            Please answer the above question.
                                        </p>
                                        <p v-if="bagCollected == false" class="warning">Please don't leave today's bag.
                                            Empty the bag and bring it back.</p>
                                    </div>
                                </div>
                            </div>

                            <div class="field">
                                <p class="control">
                                    <input v-model="notes" class="input" :class="[isNotesRequired ? 'is-danger' : '']" type="
                            text" placeholder="Any remarks? e.g., Not at home">
                                </p>
                                <p v-if="isNotesRequired" class="help is-danger">Please specify the reason</p>
                            </div>

                            <div class="align-center block">
                                <button v-if="selfCollection" @click="saveDelivery('SELF_PICKUP')"
                                    class="button is-info is-rounded is-large">
                                    Self Collected
                                </button>
                            </div>

                            <div class="field is-grouped is-grouped-centered">
                                <p class="control">
                                    <button @click="saveDelivery('DELIVERED')" class="button is-success is-rounded">
                                        Delivered
                                    </button>
                                </p>
                                <p class="control">
                                    <button @click="saveDelivery('NOT_DELIVERED')" class="button is-danger is-rounded">
                                        Not Delivered
                                    </button>
                                </p>
                            </div>
                        </div>
                        <div v-else>
                            <div v-if="status == 'SELF_PICKUP' && !isCurrentUserAdmin" class="align-center">
                                <p>Beneficiary Self Collected at the Void Deck.</p>
                                <p>No need to deliver. Please return the bag.</p>
                            </div>
                            <button v-else @click="toggleUpdateDeliveryButtons(true)" class="button is-info is-rounded">
                                Update
                            </button>
                        </div>

                    </div>

                    <div v-if="showEditDeliveryComponent">
                        <EditDelivery :fdID="this.fdID" :deliveryID="this.beneficiaryID"
                            :selfCollection="this.selfCollection" :drivingDelivery="this.drivingDelivery"
                            :drivingTeams="this.drivingTeams" :walkingTeams="this.walkingTeams"
                            :deliveryTeam="deliveryTeam"></EditDelivery>
                    </div>

                    <div v-else-if="fdStatus == 'DRAFT'" class="align-center block">
                        <button class="button is-info is-rounded" @click="editDelivery">
                            Edit
                        </button>
                    </div>

                </div>
            </footer>
        </div>
    </div>
</template>
<script>
import EditDelivery from './EditDelivery.vue';

export default {
    components: {
        EditDelivery
    },
    data() {
        return {
            showDeliveryUpdateTab: false,
            showEditDeliveryComponent: false,
            notes: "",
            isNotesRequired: false,
            isLoading: false,
            error: "",
            canUpdateDelivery: false,
            bagCollected: null,
            isBagCollectionRequired: false,
            editableAdminNotes: '',
            isSavingAdminNotes: false
        }
    },
    props: ['name', 'dryBag', 'quantity', 'unit', 'block', 'street', 'completed', 'completedAtTime', 'age', 'deliverySort', 'fdID', 'fdStatus',
        'beneficiaryID', 'status', 'selfCollection', 'drivingDelivery', 'deliveryNotes', 'deliveryTeam', 'drivingTeams', 'walkingTeams', 'bagStatus',
        'consecutiveUndeliveredCount', 'dietaryRequirement', 'adminNotes'],
    methods: {
        toggleDeliveryUpdateTab() {
            if (this.showDeliveryUpdateTab) {
                this.canUpdateDelivery = false;
            } else {
                this.editableAdminNotes = this.adminNotes || '';
            }
            this.showDeliveryUpdateTab = this.showDeliveryUpdateTab ? false : true;
        },
        toggleUpdateDeliveryButtons(newValue) {
            this.canUpdateDelivery = newValue;
        },
        isFormValid(status) {
            var formValid = true;
            if (this.isReusableBagFeatureEnabled && !this.selfCollection && this.bagStatus != 'HAS_NO_BAG' && this.bagCollected == null && status != 'NOT_DELIVERED') {
                this.isBagCollectionRequired = true;
                formValid = false;
            }
            if (status == 'NOT_DELIVERED' && !this.notes) {
                this.isNotesRequired = true;
                formValid = false;
            }
            return formValid;
        },
        async saveDelivery(status) {

            if (this.isFormValid(status)) {
                this.isNotesRequired = false;
                this.isBagCollectionRequired = false;
                this.error = null;
                this.isLoading = true;
                try {
                    await this.$store.dispatch('delivery/updateDelivery', {
                        fdID: this.fdID,
                        beneficiaryID: this.beneficiaryID,
                        notes: this.notes,
                        status: status,
                        selfCollection: this.selfCollection,
                        drivingDelivery: this.drivingDelivery,
                        updateCommand: this.canUpdateDelivery
                    });
                    this.deliverySubmitted = true;
                } catch (error) {
                    this.error = error.message || 'Something went wrong!';
                    this.deliverySubmitted = false;
                }
                if (this.isReusableBagFeatureEnabled) {
                    try {
                        await this.$store.dispatch('beneficiary/updateBagStatus', {
                            beneficiaryID: this.beneficiaryID,
                            deliveryStatus: status,
                            bagStatus: this.bagStatus,
                            bagCollected: this.bagCollected
                        });
                    } catch (error) {
                        this.error = error.message || 'Something went wrong!';
                    }
                }
                this.isLoading = false;
                if (!this.error) {
                    this.notes = "";
                    this.toggleUpdateDeliveryButtons(false);
                    this.toggleDeliveryUpdateTab();
                }
            }
        },
        async saveAdminNotes() {
            this.isSavingAdminNotes = true;
            try {
                await this.$store.dispatch('delivery/updateDeliveryNotes', {
                    fdID: this.fdID,
                    beneficiaryID: this.beneficiaryID,
                    deliveryNotes: this.editableAdminNotes
                });
            } catch (error) {
                this.error = error.message || 'Something went wrong!';
            }
            this.isSavingAdminNotes = false;
        },
        editDelivery() {
            this.showEditDeliveryComponent = true;
        },
        getStatusClass(status) {
            if (status === "DELIVERED") {
                return "is-primary is-light";
            } else if (status === "NOT_DELIVERED") {
                return "is-danger is-light";
            } else if (status === "SELF_PICKUP") {
                return "is-info is-light";
            } else {
                return "";
            }
        }
    },
    computed: {
        isCurrentUserAdmin() {
            return this.$store.getters['user/isCurrentUserAdmin'];
        },
        isReusableBagFeatureEnabled() {
            return this.$store.getters['configurations/isReusableBagFeatureEnabled'];
        },
        isDisplayDietaryRequirementEnabled() {
            return this.$store.getters['configurations/isDisplayDietaryRequirementEnabled'];
        },
        editLink() {
            return "/beneficiary/" + this.beneficiaryID;
        }
    }
}
</script>

<style scoped>
.delivery-right {
    display: flex;
    align-items: center; /* Aligns items vertically in the center */
    gap: 1rem; /* Adds spacing between the <p> elements */
}
img {
    height: 30px;
}
.button {
    font-size: medium;
    justify-content: space-between;
}
.warning {
    color: red;
}
.dynamic-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.3rem;
    height: 1.3rem;
    border-radius: 50%;
    font-size: 0.8rem;
    font-weight: bold;
    color: white;
}

.circle-1 {
    background-color: yellow;
    color: black; /* Adjust text color for contrast */
}

.circle-2 {
    background-color: orange;
}

.circle-3 {
    background-color: red;
}
/* Strikethrough style */
.strikethrough {
    text-decoration: line-through;
}

</style>